import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, Dimensions, ImageStyle, ScrollView, TextStyle, ViewStyle } from "react-native"
import { AutoImage, Button, JalaIcon, Screen, Text } from "../../../components"
import { CompositeScreenProps, useLinkTo } from "@react-navigation/native"
import { useStores } from "../../../models"
import { View } from "native-base"
import { color } from "../../../theme"
import { presets } from "../../../components/text/text.presets"
import { LinearGradient } from "expo-linear-gradient"
import { translate } from "../../../i18n"
import {
  QuestionWithAnswerDetailScreen,
} from "../question/question-with-answer-detail/question-with-answer-detail-screen"
import config from "../../../utils/config"
import moment from "moment"
import html2canvas from "html2canvas"

const height = Dimensions.get("window").height
const width = Dimensions.get("window").width - 32

const ROOT: ViewStyle = {
  flexGrow: 1,
}

const CONTAINER: ViewStyle = {
  // flex: 1,
  paddingTop: 15,
  justifyContent: "center",
  minHeight: height,
}

const IMAGE: ImageStyle = {
  width: Dimensions.get("window").width - 32,
  height: 100,
}

const IMG_SPARKLING: ImageStyle = {
  position: "absolute",
  bottom: 28,
  left: 10,
}

const IMG_SPARKLING_2: ImageStyle = {
  position: "absolute",
  right: -13,
  top: 56,
}

const IMG_SMALL_SPARKLING: ImageStyle = {
  position: "absolute",
  top: 7,
  left: 44,
}

const SCORE_TEXT: TextStyle = {
  ...presets().title4,
  fontSize: 85,
}

const SCORE_INFO: TextStyle = {
  ...presets().title4,
  fontSize: 21,
}

const PADDING: ViewStyle = {
  paddingHorizontal: 16,
  paddingBottom: 50,
}

const IMG_PROFILE: ImageStyle = {
  width: 34,
  height: 34,
  borderRadius: 34 / 2,
}

const SHARE_BUTTON: ViewStyle = {
  alignSelf: "flex-end",
  marginRight: 16,
  marginBottom: 20,
}

const getBase64ImageFromUrl = url => fetch(url).then(response => response.blob()).then(blob => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onloadend = () => resolve(reader.result)
  reader.onerror = reject
  blob = new Blob([blob], { type: "image/png" })
  return reader.readAsDataURL(blob)
}))


export const ResultScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function ResultScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const linkTo = useLinkTo()

  const scrollRef = useRef<ScrollView>()
  const igpost = useRef<HTMLElement>(null)

  // Pull in one of our MST stores
  const {
    quizStore: {
      questions, answers, times,
    },
    userStore: { user },
  } = useStores()

  const [result, setResult] = useState<Array<any>>([])
  const [image, setImage] = useState<any>(null)
  const [title, setTitle] = useState<string>(null)
  const [hidden, setHidden] = useState<boolean>(false)
  const [avatar, setAvatar] = useState<string>(null)

  const { BASE_URL } = config()

  const imageTimer = require("./timer.png")

  const time = useMemo(() => {
    return moment(times[1] - times[0]).format("mm'ss\"SSS")
  }, [times])

  const titleShare = useMemo<string>(() => {
    const bad = result.length <= 3
    return hidden && bad ? translate("startQuizScreen.badShareTitle") : title
  },[hidden, result.length, title])

  const scoreTitle = useMemo<string>(() => {
    return result.length !== 0 ? translate("startQuizScreen.score", { result: `${result.length}` }) : translate("startQuizScreen.worstScore")
  },[result.length])


  useEffect(() => {
    getBase64ImageFromUrl(`${BASE_URL}/img/cache/medium/${user.avatar}?${moment().valueOf()}`)
      .then((result: string) => setAvatar(result))
  }, [BASE_URL, user?.avatar])

  useEffect(() => {
    let image
    let title
    const output = questions.filter(question => answers.some(answer => answer.id === question.fields.id && answer.answerKey === question.fields.key))
    if (output.length === questions.length) {
      image = require("./perfect.png")
      title = translate("startQuizScreen.perfectTitle")
    } else if (output.length >= 7 && output.length <= 9) {
      image = require("./excellent.png")
      title = translate("startQuizScreen.excellentTitle")
    } else if (output.length <= 6 && output.length >= 4) {
      image = require("./good.png")
      title = translate("startQuizScreen.goodTitle")
    } else if (output.length === 0) {
      image = require("./worst.png")
      title = translate("startQuizScreen.badTitle")
    } else {
      image = require("./bad.png")
      title = translate("startQuizScreen.badTitle")
    }
    setResult(output)
    setImage(image)
    setTitle(title)
  }, [answers, questions])

  const handleScroll = () => scrollRef.current.scrollToEnd({ animated: true })

  const onShare = useCallback(() => {
    setHidden(true)
    setTimeout(() => {
      html2canvas(igpost.current, {
        useCORS: true,
        allowTaint: true,
      })
        .then(canvas => Promise.all([
          canvas.toDataURL("image/jpeg", 1.0),
          new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 1.0))
        ]))
        .then(async ([imageBase64, blob]) => {
          if ('ReactNativeWebView' in window && (window as any).ReactNativeWebView.postMessage) {
            (window as any).ReactNativeWebView.postMessage(JSON.stringify({
              action: 'share',
              payload: { title: '', url: imageBase64 }
            }))
          } else if ('navigator' in window && window.navigator.share) {
            await window.navigator.share({
              title: '',
              text: '',
              files: [blob]
            } as any).catch()
          } else {
            const a = document.createElement("a")
            a.href = imageBase64
            a.download = `${user.name}-${result.length}.jpg`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          }
        }).finally(() => {
          setHidden(false);
        })
    }, 4000)
  }, [result.length, user?.name])

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      scrollViewRef={scrollRef}
    >
      <View ref={igpost}>
        <LinearGradient
          colors={["#0082F3", "#45AAE3"]}
          style={CONTAINER}
          start={{ x: 0.0, y: 0.0 }}
          end={{ x: 1, y: 1 }}
        >
          {!hidden && <Button testID="jala:id/share-button" style={SHARE_BUTTON} preset="link" onPress={onShare}>
            <JalaIcon name="share" size={25} color={color.palette.white} />
          </Button>}
          <View alignItems="center">
            <View flexDirection="row" mb="48px" paddingX="16px" justifyContent="space-between" width={width}>
              <View flex={1} flexDirection="row" alignItems="center">
                <AutoImage source={{ uri: avatar }} style={IMG_PROFILE} />
                <Text preset="title4" text={user.name} mx="8px" numberOfLines={1} ellipsizeMode="tail"
                      color={color.palette.white} colorScheme={colorScheme} />
              </View>
              <View flexDirection="row" alignItems="center">
                <AutoImage source={imageTimer} />
                <Text preset="display3" text={`${time}`} ml="7px" color={color.palette.white}
                      colorScheme={colorScheme} />
              </View>
            </View>
            <View position="relative" width="196px" height="196px" mb="17.33px" alignItems="center"
                  justifyContent="center" borderRadius="98px" backgroundColor="#68ABF5E8">
              <View
                backgroundColor={color.palette.white}
                width="166px"
                height="166px"
                borderWidth="5px"
                borderColor={color.palette.orange500}
                borderRadius="83px"
                alignItems="center"
                justifyContent="center"
              >
                <Text style={SCORE_TEXT} color={color.palette.orange500} text={`${result.length}`}
                      colorScheme={colorScheme} />
              </View>
              <AutoImage source={require("./smallSparkling.png")} style={IMG_SMALL_SPARKLING} />
              <AutoImage source={require("./sparkling.png")} style={IMG_SPARKLING} />
              <AutoImage source={require("./sparkling.png")} style={IMG_SPARKLING_2} />
            </View>
            <AutoImage source={image} style={IMAGE} />
            <Text
              text={scoreTitle}
              mx="16px"
              mt="26px"
              color={color.palette.white}
              textAlign="center"
              style={SCORE_INFO}
              colorScheme={colorScheme}
            />
            <Text
              preset="body1"
              text={titleShare}
              colorScheme={colorScheme}
              color={color.palette.white}
              textAlign="center"
              mx="16px"
              mt="20px"
            />
          </View>
          {!hidden && <>
            <View px="35px" mt="55px" mb="44px">
              <Button tx="startQuizScreen.learnQuiz"
                      colorScheme={colorScheme}
                      style={{ backgroundColor: color.palette.orange500 }}
                      textColor={color.palette.white}
                      onPress={handleScroll}
              />
              <View height="20px" />
              <Button
                tx="startQuizScreen.backTo"
                colorScheme={colorScheme}
                style={{ backgroundColor: color.palette.white }}
                textColor={color.primary}
                onPress={() => linkTo("/Quiz.Start")}
              />
            </View>
            <View borderBottomWidth="1px" borderColor={color.palette.white} mx="16px" />
          </>}
        </LinearGradient>
      </View>
      <LinearGradient
        colors={["#0082F3", "#45AAE3"]}
        style={[CONTAINER, PADDING]}
        start={{ x: 1, y: 1 }}
        end={{ x: 0.0, y: 0.0 }}
      >
        <QuestionWithAnswerDetailScreen
          colorScheme={colorScheme}
          scrollRef={scrollRef}
        />
      </LinearGradient>
    </Screen>
  )
})
